import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

const STORAGE_KEY = 'laptura_accent';

/**
 * Accent palettes as RGB channel triplets for --lap-* CSS vars. Avoids gain/loss/highlight.
 *
 * A theme may also carry `brand`, which re-skins the app wordmark for a racing
 * series (see BrandMark). `accent` is the series' exact published brand color;
 * `bright` is a lightened tint because that token is used for small text on the
 * dark background and the brand color alone does not clear 4.5:1 there.
 */
export const ACCENT_THEMES = {
  blue: { label: 'Blue', accent: '59 130 246', bright: '91 168 255', pressed: '47 111 224' },
  violet: { label: 'Purple', accent: '168 85 247', bright: '216 180 254', pressed: '147 51 234' },
  cyan: { label: 'Cyan', accent: '6 182 212', bright: '34 211 238', pressed: '8 145 178' },
  green: { label: 'Green', accent: '5 150 105', bright: '52 211 153', pressed: '4 120 87' },
  orange: { label: 'Orange', accent: '249 115 22', bright: '251 146 60', pressed: '234 88 12' },
  pink: { label: 'Pink', accent: '236 72 153', bright: '244 114 182', pressed: '219 39 119' },

  // IMSA — 2023 IMSA Brand Book, "Color palettes" (p.16).
  // Primary: PMS 1797 / CMYK 0-100-99-4 / RGB 227 27 35.
  imsa: {
    label: 'IMSA',
    accent: '227 27 35',
    bright: '255 92 99',
    pressed: '196 20 28',
    brand: { wordmark: 'IMSA', tagline: 'powered by Laptura' },
  },

  // Mazda MX-5 Cup — no published brand book for the series, so the red is
  // sampled from the official series lockup (RGB 207 32 48 / #CF2030, the
  // dominant non-black color in the artwork).
  mx5: {
    label: 'MX-5',
    accent: '207 32 48',
    bright: '255 90 104',
    pressed: '178 28 41',
    brand: { wordmark: 'MX-5 CUP', tagline: 'powered by Laptura' },
  },
};

export const ACCENT_IDS = Object.keys(ACCENT_THEMES);

/** Plain color accents (no wordmark change). */
export const COLOR_ACCENT_IDS = ACCENT_IDS.filter((id) => !ACCENT_THEMES[id].brand);
/** Series themes that also re-skin the wordmark. */
export const SERIES_ACCENT_IDS = ACCENT_IDS.filter((id) => ACCENT_THEMES[id].brand);

/** Brand lockup for the active theme, or null for stock Laptura. */
export function getAccentBrand(id) {
  return getAccentTheme(id).brand || null;
}

function rgbChannelsToHex(channels) {
  const [r, g, b] = String(channels).trim().split(/\s+/).map(Number);
  if (![r, g, b].every((n) => Number.isFinite(n))) return '#3B82F6';
  return `#${[r, g, b].map((n) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0')).join('')}`;
}

export function getAccentTheme(id) {
  return ACCENT_THEMES[id] || ACCENT_THEMES.blue;
}

export function getAccentHex(kind = 'accent', id) {
  const theme = getAccentTheme(id || (typeof document !== 'undefined' ? document.documentElement.dataset.accent : 'blue'));
  const channels = kind === 'bright' ? theme.bright : kind === 'pressed' ? theme.pressed : theme.accent;
  return rgbChannelsToHex(channels);
}

export function applyAccentTheme(id) {
  const resolved = ACCENT_THEMES[id] ? id : 'blue';
  const theme = ACCENT_THEMES[resolved];
  if (typeof document === 'undefined') return resolved;
  const root = document.documentElement;
  root.style.setProperty('--lap-accent', theme.accent);
  root.style.setProperty('--lap-accent-bright', theme.bright);
  root.style.setProperty('--lap-pressed', theme.pressed);
  root.dataset.accent = resolved;
  return resolved;
}

function getStoredAccent() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && ACCENT_THEMES[stored]) return stored;
  } catch (_) {}
  return 'blue';
}

if (typeof document !== 'undefined') {
  applyAccentTheme(getStoredAccent());
}

const AccentThemeContext = createContext(null);

export function AccentThemeProvider({ children }) {
  const [accent, setAccentState] = useState(getStoredAccent);

  const setAccent = useCallback((id) => {
    if (!ACCENT_THEMES[id]) return;
    const resolved = applyAccentTheme(id);
    setAccentState(resolved);
    try {
      localStorage.setItem(STORAGE_KEY, resolved);
    } catch (_) {}
  }, []);

  const value = useMemo(() => ({ accent, setAccent, themes: ACCENT_THEMES }), [accent, setAccent]);
  return (
    <AccentThemeContext.Provider value={value}>
      {children}
    </AccentThemeContext.Provider>
  );
}

export function useAccentTheme() {
  const ctx = useContext(AccentThemeContext);
  if (!ctx) {
    return { accent: 'blue', setAccent: () => {}, themes: ACCENT_THEMES };
  }
  return ctx;
}
